import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useCallback } from "react";
import Grid from "@material-ui/core/Grid";
import FormHelperText from "@material-ui/core/FormHelperText";
import Icon from "@material-ui/core/Icon";
import IconButton from "@material-ui/core/IconButton";
import HelpIcon from "@material-ui/icons/Help";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import CachedIcon from "@material-ui/icons/Cached";
import { useTenant, useTenantImage, useThemeProps } from "@ploy-ui/tenants";
import { Field as FormikField, useFormikContext } from "formik";
import CalculationField from "./CalculationField";
import { mapFieldWidthToGridSize, ErrorDisplay } from "@ploy-lib/types";
import { makeStyles } from "@material-ui/core/styles";
import { getFieldName } from "@ploy-lib/core";
import clsx from "clsx";
import { DployFormControl, getFieldError, getNoTouchError } from "@ploy-ui/form-fields";
import { useWriteLockedFields, useDispatch, useGetDebugFieldProps } from "@ploy-lib/calculation";
import { ImageFit, MarkdownElement } from "@ploy-ui/core";
import { defineMessages } from "react-intl";
import { Box, useMediaQuery, useTheme, Divider } from "@material-ui/core";
import { fieldToKey, isNotNull } from "../utils";
const useStyles = makeStyles((theme) => ({
    root: {},
    rowContainer: {},
    field: {},
    fieldSeparator: {},
    fieldWrapper: {
        position: "relative"
    },
    alignCenterInParent: {
        alignSelf: "center"
    },
    styleTooltipContainer: {
        display: "flex",
        alignItems: "center",
        cursor: "pointer"
    },
    styleTooltip: {
        alignItems: "center"
    },
    styleHelperText: {
        // Possible slightly brittle this one ^^'
        marginTop: -3,
        "& p, & ul, & ol": {
            fontSize: "0.85rem"
        },
        "& p": {
            marginTop: -4,
            marginBottom: 12,
            whiteSpace: "pre-line"
        }
    },
    collapsedSectionField: {
        color: theme.palette.text.disabled
    },
    errorContainer: {
        marginTop: -14
    },
    divider: {
        margin: "0px"
    },
    underline: {
        textDecorationLine: "underline"
    },
    inlineHelperTextContainer: {
        paddingLeft: 5
    },
    inlineHelperTextIconButton: {
        padding: 0,
        pointerEvents: "all"
    },
    helperTextIcon: {}
}), { name: "DployFormFieldContainer" });
function useResetWriteLocked(fields) {
    const dispatch = useDispatch();
    const resetFields = useWriteLockedFields(...fields.filter(f => f.canResetWriteLocked));
    const resetWriteLocked = useCallback(() => {
        const patches = resetFields
            .filter(isNotNull)
            .filter(calcField => calcField.writeLocked)
            .map((calcField) => calcField.namespace && calcField.variableName
            ? {
                changeTrigger: "CHECK_" + calcField.variableName,
                namespace: calcField.namespace,
                target: calcField.variableName,
                writeLocked: false
            }
            : null)
            .filter(isNotNull);
        dispatch({
            type: "patch",
            payload: { patches }
        });
    }, [resetFields, dispatch]);
    const canReset = resetFields
        .filter(isNotNull)
        .filter(x => x.namespace && x.variableName)
        .filter(calcField => calcField.writeLocked).length > 0;
    return canReset ? resetWriteLocked : undefined;
}
const FieldContainer = (props) => {
    const { className, literal, justifyContent, wrap, fields, disabled, lowContrastText, linkedHelperText = false, desktopViewForMobile, inlineLabelHelperText = false } = useThemeProps(props, "DployFormFieldContainer");
    const [first] = fields;
    const { errors, touched } = useFormikContext();
    const variant = first.variant && ["outlined", "filled", "standard"].includes(first.variant)
        ? first.variant
        : "outlined";
    const helperText = fields
        .filter(f => f.helperText)
        .filter(f => !(f.literal || literal) || f.showHelperTextIfLiteral)
        .filter(f => !f.disabled)
        .map(f => f.helperText)
        .join("\n");
    const trimmedHelperText = helperText[0] === "!" ? helperText.substring(1) : helperText;
    const error = fields
        .map(f => getFieldError(errors, touched, getFieldName(f), f.literal || literal
        ? f.errorDisplayLiteral ?? ErrorDisplay.Never
        : f.errorDisplay ?? ErrorDisplay.Touched))
        .filter(e => e.error && e.helperText && e.helperText.length > 0);
    //
    const [showHelperText, setShowHelperText] = useState(helperText[0] === "!");
    const toggleHelperText = useCallback((e) => {
        setShowHelperText(show => !show);
        e.stopPropagation();
    }, []);
    const theme = useTheme();
    const displayHelperTextAboveField = useTenant().tenant?.settings?.displayHelperTextAboveField ?? false;
    const mobileMediaQuery = useMediaQuery(theme.breakpoints.down("xs"));
    const isMobile = desktopViewForMobile ? false : mobileMediaQuery;
    const classes = useStyles(props);
    const getDebugFieldProps = useGetDebugFieldProps();
    const resetWriteLocked = useResetWriteLocked(literal ? [] : fields.filter(f => !f.literal));
    const resetButton = resetWriteLocked ? (_jsx(IconButton, { onClick: resetWriteLocked, children: _jsx(CachedIcon, { fontSize: "small" }) })) : undefined;
    const leftPartOfMargin = fields[0].margin?.split(" ").slice(-1)[0];
    return (_jsxs(_Fragment, { children: [_jsx(Grid, { className: clsx(classes.root, className), item: true, xs: (literal && trimmedHelperText && !inlineLabelHelperText) ||
                    first.fullWidth ||
                    inlineLabelHelperText
                    ? 12
                    : 11, children: _jsxs(Grid, { className: classes.rowContainer, container: true, spacing: 1, justifyContent: justifyContent, wrap: wrap, children: [fields.map((field, idx, arr) => {
                            const { label: fieldLabel, name: fieldName, width, margin = "normal", uppercaseLabel } = field;
                            let { hiddenLabel } = field;
                            const name = getFieldName(field);
                            const defaultGridSize = (12 /
                                Math.min(4, fields.length));
                            const fieldIsLiteral = field.literal;
                            const isFirst = idx === 0;
                            const lastField = idx === arr.length - 1;
                            const labelText = hiddenLabel
                                ? " "
                                : uppercaseLabel && fieldLabel
                                    ? fieldLabel.toLocaleUpperCase().trim()
                                    : fieldLabel?.trim();
                            const labelComponent = linkedHelperText ||
                                !trimmedHelperText || // uten helper text trenger man ikke noe mer enn label
                                literal || // literal fields ordner HelperTextIconButton selv
                                !inlineLabelHelperText ? (labelText) : (_jsx(LabelComponent, { labelText: labelText, fieldIsLiteral: fieldIsLiteral, isFirst: isFirst, trimmedHelperText: trimmedHelperText, displayHelperTextAboveField: displayHelperTextAboveField, variant: variant, field: first, showHelperText: showHelperText, toggleHelperText: toggleHelperText }));
                            return (_jsxs(React.Fragment, { children: [_jsxs(Grid, { style: {
                                            margin: field.margin ? field.margin : undefined,
                                            display: "flex"
                                        }, item: true, xs: isMobile
                                            ? 12
                                            : width
                                                ? mapFieldWidthToGridSize(width)
                                                : defaultGridSize, "data-cr-field": field.name, "data-cr-namespace": field.namespace, "data-cr-component": field.renderAs, ...getDebugFieldProps(field, {
                                            className: clsx(classes.fieldWrapper, classes.field, {
                                                [classes.fieldSeparator]: !lastField,
                                                [classes.alignCenterInParent]: field.alignCenterInParent,
                                                [classes.underline]: field.underline
                                            })
                                        }), children: [_jsx(FormikField, { ...field, style: {
                                                    fontSize: field.fontSize ? field.fontSize : undefined,
                                                    height: field.padding ? "auto" : undefined,
                                                    padding: field.padding ? field.padding : undefined
                                                }, errorDisplay: getNoTouchError(field.errorDisplay ?? ErrorDisplay.TouchedNoMessage), errorDisplayLiteral: getNoTouchError(field.errorDisplayLiteral ?? ErrorDisplay.Never), name: name, component: CalculationField, fieldName: fieldName, margin: margin, className: clsx(classes.field, {
                                                    [classes.collapsedSectionField]: lowContrastText
                                                }), label: labelText, labelComponent: (labelText?.trim()?.length || 0) > 0 ? labelComponent : null, literal: literal || field.literal, justifyContent: justifyContent || field.justifyContent, helperText: undefined, buttonText: fieldLabel, disabled: disabled || field.disabled }), !(disabled || field.disabled) && resetButton && lastField && (_jsx(Box, { position: "absolute", top: -5, p: 0.5, right: -26, children: resetButton }))] }, fieldToKey(field)), field.useFieldSeperator && literal && (_jsx(Grid, { item: true, xs: 12, children: _jsx(Divider, { className: classes.divider, orientation: "horizontal" }) }))] }, `${fieldToKey(field)}_${idx}`));
                        }), linkedHelperText &&
                            trimmedHelperText &&
                            !literal &&
                            !inlineLabelHelperText && (_jsx(HelperTextIconButton, { showHelperText: showHelperText, toggleHelperText: toggleHelperText, firstField: first }))] }) }), !linkedHelperText &&
                trimmedHelperText &&
                !literal &&
                !inlineLabelHelperText && (_jsx(HelperTextIconButton, { showHelperText: showHelperText, toggleHelperText: toggleHelperText, firstField: first })), error.length > 0 && (_jsx(Grid, { item: true, xs: isMobile || fields.length > 1 || !first.width
                    ? 12
                    : mapFieldWidthToGridSize(first.width), className: classes.errorContainer, style: leftPartOfMargin ? { marginLeft: leftPartOfMargin } : {}, children: error.map((err, i) => (_jsx(FormHelperText, { variant: variant, error: true, children: err.helperText }, i))) })), showHelperText &&
                trimmedHelperText &&
                (!displayHelperTextAboveField || first.literal) && (_jsx(Grid, { item: true, xs: isMobile ||
                    fields.length > 1 ||
                    !first.width ||
                    first.useFullWidthForHelperText
                    ? 12
                    : mapFieldWidthToGridSize(first.width), style: leftPartOfMargin ? { marginLeft: leftPartOfMargin } : {}, children: _jsx(FormHelperTextContainerWrapper, { helperText: trimmedHelperText, variant: variant }) }))] }));
};
export const messages = defineMessages({
    Helper: {
        id: "template-form.field-container.helper",
        description: "Press for help text",
        defaultMessage: "Press for help text"
    }
});
FieldContainer.displayName = "DployFormFieldContainer";
export default FieldContainer;
export { FieldContainer };
export const HelperTextIconButton = (props) => {
    const { firstField, toggleHelperText, showHelperText, inline = false } = props;
    const classes = useStyles();
    const tenantHelperTextIcon = useTenantImage("helperText");
    const tenantHelperTextOpenIcon = useTenantImage("helperTextOpen");
    return (_jsx(Grid, { item: true, xs: inline ? "auto" : 1, className: clsx(classes.styleTooltipContainer, {
            [classes.inlineHelperTextContainer]: inline
        }), children: _jsx(DployFormControl, { variant: firstField?.variant, className: classes.styleTooltip, children: _jsx(IconButton, { onClick: toggleHelperText, className: inline ? classes.inlineHelperTextIconButton : undefined, "aria-label": "Open or close helper text", children: _jsx(Icon, { className: classes.helperTextIcon, children: showHelperText ? (tenantHelperTextOpenIcon ? (_jsx(ImageFit, { style: { height: "100%" }, src: tenantHelperTextOpenIcon, fit: "contain", position: "bottom" })) : (_jsx(HelpIcon, {}))) : tenantHelperTextIcon ? (_jsx(ImageFit, { style: { height: "100%" }, src: tenantHelperTextIcon, fit: "contain", position: "bottom" })) : (_jsx(HelpOutlineIcon, {})) }) }) }) }));
};
export const FormHelperTextContainerWrapper = (props) => {
    const { helperText, variant } = props;
    const classes = useStyles();
    return (_jsx(FormHelperText, { variant: variant, error: false, children: _jsx("div", { className: classes.styleHelperText, children: _jsx(MarkdownElement, { text: helperText }) }) }));
};
export const LabelComponent = (props) => {
    const { labelText, fieldIsLiteral, isFirst = true, trimmedHelperText, displayHelperTextAboveField = true, variant = "outlined", field, showHelperText, toggleHelperText } = props;
    return (_jsxs(_Fragment, { children: [_jsxs(Grid, { container: true, direction: "row", alignItems: "center", style: { flexWrap: "nowrap" }, children: [_jsx("span", { style: { overflowWrap: "anywhere" }, children: labelText }), _jsx(HelperTextIconButton, { inline: true, showHelperText: showHelperText, toggleHelperText: toggleHelperText, firstField: field })] }), showHelperText &&
                trimmedHelperText &&
                displayHelperTextAboveField &&
                isFirst &&
                !fieldIsLiteral && (_jsx(Grid, { item: true, xs: 12, children: _jsx(FormHelperTextContainerWrapper, { helperText: trimmedHelperText, variant: variant }) }))] }));
};
//# sourceMappingURL=FieldContainer.js.map